import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import ApiIcon from '@mui/icons-material/Api';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SecurityIcon from '@mui/icons-material/Security';
import MapIcon from '@mui/icons-material/Map';

import PostAddIcon from '@mui/icons-material/PostAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { gridTheme, gridTokens } from '../theme/gridTheme';

// Recolor filters copied verbatim from the website header CSS (styles.css).
// Black source art -> white text, and black -> accent cyan for the icon.
const LOGO_TEXT_FILTER = 'brightness(0) invert(1)';
const LOGO_ICON_FILTER =
  'brightness(0) saturate(100%) invert(72%) sepia(94%) saturate(2476%) hue-rotate(126deg) brightness(101%) contrast(101%)';

// Shared styling for the text-style nav buttons in the bar.
const navButtonSx = {
  color: gridTokens.textSecondary,
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '0.9rem',
  px: 1.5,
  borderRadius: `${gridTokens.radius}px`,
  '&:hover': {
    color: gridTokens.textPrimary,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
};

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [locationsExpanded, setLocationsExpanded] = useState(false);
  const [postsExpanded, setPostsExpanded] = useState(false);
  const [usersExpanded, setUsersExpanded] = useState(false);
  const [locationsAnchor, setLocationsAnchor] = useState<null | HTMLElement>(null);
  const [postsAnchor, setPostsAnchor] = useState<null | HTMLElement>(null);
  const [usersAnchor, setUsersAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const closeDrawer = () => setDrawerOpen(false);

  const handleDrawerNavigate = (path: string) => {
    navigate(path);
    closeDrawer();
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleUserMenuClose();
  };

  const handleLogout = async () => {
    handleUserMenuClose();

    try {
      // Call backend logout endpoint to clear HttpOnly cookies
      const apiBaseUrl = localStorage.getItem('useRemoteApi') === 'true'
        ? process.env.REACT_APP_REMOTE_API_URL
        : process.env.REACT_APP_LOCAL_API_URL;

      await fetch(`${apiBaseUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Required to send HttpOnly cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear any old localStorage tokens (cleanup)
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('lastTokenTime');

    // Clear cookies if they exist (for non-HttpOnly cookies)
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const [name] = cookie.trim().split('=');
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    // Navigate to login page
    navigate('/login');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleLocationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setLocationsAnchor(event.currentTarget);
  };

  const handleLocationsClose = () => {
    setLocationsAnchor(null);
  };

  const handleLocationOption = (path: string) => {
    navigate(path);
    setLocationsAnchor(null);
  };

  const handlePostsClick = (event: React.MouseEvent<HTMLElement>) => {
    setPostsAnchor(event.currentTarget);
  };

  const handlePostsClose = () => {
    setPostsAnchor(null);
  };

  const handlePostOption = (path: string) => {
    navigate(path);
    setPostsAnchor(null);
  };

  const handleUsersClick = (event: React.MouseEvent<HTMLElement>) => {
    setUsersAnchor(event.currentTarget);
  };

  const handleUsersClose = () => {
    setUsersAnchor(null);
  };

  const handleUserOption = (path: string) => {
    navigate(path);
    setUsersAnchor(null);
  };

  // Website-style logo lockup: overlaid icon (cyan) + text (white).
  const logoLockup = (
    <Box
      onClick={handleLogoClick}
      sx={{
        position: 'relative',
        height: 40,
        aspectRatio: '1015 / 447',
        cursor: 'pointer',
        mr: 2,
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        '&:hover': { opacity: 0.85, transform: 'translateY(-1px)' },
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/grid-logo-icon-only.png`}
        alt="Grid"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          objectPosition: 'left center',
          filter: LOGO_ICON_FILTER,
        }}
      />
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/grid-logo-text-only.png`}
        alt="Grid"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          objectPosition: 'left center',
          filter: LOGO_TEXT_FILTER,
        }}
      />
    </Box>
  );

  const drawerContent = (
    <Box sx={{ width: 280, bgcolor: gridTokens.bgPrimary, height: '100%' }} role="presentation">
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => setLocationsExpanded(!locationsExpanded)}>
            <ListItemIcon><MapIcon /></ListItemIcon>
            <ListItemText primary="Locations" />
            {locationsExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={locationsExpanded}>
          <List component="div" disablePadding>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/locations?tab=editor')}>
              <ListItemText primary="Location Editor" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/locations?tab=all')}>
              <ListItemText primary="All Locations" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/locations?tab=places')}>
              <ListItemText primary="Places" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/locations?tab=neighborhood-editor')}>
              <ListItemText primary="Neighborhood Editor" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/locations?tab=google-places-import')}>
              <ListItemText primary="Import Google Places" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/quick-place-import')}>
              <ListItemText primary="Quick Place Import" />
            </ListItemButton>
          </List>
        </Collapse>

        <ListItem disablePadding>
          <ListItemButton onClick={() => setPostsExpanded(!postsExpanded)}>
            <ListItemIcon><PostAddIcon /></ListItemIcon>
            <ListItemText primary="Posts" />
            {postsExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={postsExpanded}>
          <List component="div" disablePadding>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/posts?tab=all')}>
              <ListItemText primary="All Posts" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/posts?tab=null-users')}>
              <ListItemText primary="Posts with Null Users" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/mock-posts')}>
              <ListItemText primary="Mock Posts" />
            </ListItemButton>
          </List>
        </Collapse>

        <ListItem disablePadding>
          <ListItemButton onClick={() => setUsersExpanded(!usersExpanded)}>
            <ListItemIcon><PersonIcon /></ListItemIcon>
            <ListItemText primary="Users" />
            {usersExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={usersExpanded}>
          <List component="div" disablePadding>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/users?tab=all-users')}>
              <ListItemText primary="All Users" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/users?tab=users-list')}>
              <ListItemText primary="Users List" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/users?tab=god-view')}>
              <ListItemText primary="God View" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/users?tab=identity-options')}>
              <ListItemText primary="Identity Options" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/users?tab=profile-migration')}>
              <ListItemText primary="Profile Migration" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} onClick={() => handleDrawerNavigate('/mock-users')}>
              <ListItemText primary="Mock Users" />
            </ListItemButton>
          </List>
        </Collapse>

        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/notifications" onClick={closeDrawer}>
            <ListItemIcon><NotificationsIcon /></ListItemIcon>
            <ListItemText primary="Notifications" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/reports" onClick={closeDrawer}>
            <ListItemIcon><AssessmentIcon /></ListItemIcon>
            <ListItemText primary="Reports" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/analytics" onClick={closeDrawer}>
            <ListItemIcon><AssessmentIcon /></ListItemIcon>
            <ListItemText primary="Analytics" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/enrichment" onClick={closeDrawer}>
            <ListItemIcon><AutoFixHighIcon /></ListItemIcon>
            <ListItemText primary="Enrichment" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/interest-copy" onClick={closeDrawer}>
            <ListItemIcon><ChatBubbleOutlineIcon /></ListItemIcon>
            <ListItemText primary="Interest Copy" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/place-claims" onClick={closeDrawer}>
            <ListItemIcon><AssessmentIcon /></ListItemIcon>
            <ListItemText primary="Place Claims" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/security" onClick={closeDrawer}>
            <ListItemIcon><SecurityIcon /></ListItemIcon>
            <ListItemText primary="Security" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/country-boundaries" onClick={closeDrawer}>
            <ListItemIcon><MapIcon /></ListItemIcon>
            <ListItemText primary="Country Boundaries" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/pulse-simulator" onClick={closeDrawer}>
            <ListItemText primary="Pulse Simulator" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/api-health" onClick={closeDrawer}>
            <ListItemIcon><ApiIcon /></ListItemIcon>
            <ListItemText primary="API Health" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/settings" onClick={closeDrawer}>
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>
        <Divider sx={{ my: 1 }} />
        <ListItem disablePadding>
          <ListItemButton onClick={() => { handleProfileClick(); closeDrawer(); }}>
            <ListItemIcon><AccountCircleIcon /></ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => { handleLogout(); closeDrawer(); }}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={gridTheme}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          // Website header: dark translucent glass with blur + hairline border.
          background: 'rgba(11, 11, 13, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${gridTokens.borderPrimary}`,
          color: gridTokens.textPrimary,
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }}>
          {logoLockup}

          {isMobile ? (
            <IconButton
              aria-label="open menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ ml: 'auto', color: gridTokens.textPrimary }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Button sx={navButtonSx} onClick={handleLocationsClick} endIcon={<ExpandMore sx={{ fontSize: 16 }} />}>
                  Locations
                </Button>
                <Menu anchorEl={locationsAnchor} open={Boolean(locationsAnchor)} onClose={handleLocationsClose}>
                  <MenuItem onClick={() => handleLocationOption('/locations?tab=editor')}>Location Editor</MenuItem>
                  <MenuItem onClick={() => handleLocationOption('/locations?tab=all')}>All Locations</MenuItem>
                  <MenuItem onClick={() => handleLocationOption('/locations?tab=places')}>Places</MenuItem>
                  <MenuItem onClick={() => handleLocationOption('/locations?tab=neighborhood-editor')}>Neighborhood Editor</MenuItem>
                  <MenuItem onClick={() => handleLocationOption('/locations?tab=google-places-import')}>Import Google Places</MenuItem>
                </Menu>

                <Button sx={navButtonSx} onClick={handlePostsClick} endIcon={<ExpandMore sx={{ fontSize: 16 }} />}>
                  Posts
                </Button>
                <Menu anchorEl={postsAnchor} open={Boolean(postsAnchor)} onClose={handlePostsClose}>
                  <MenuItem onClick={() => handlePostOption('/posts?tab=all')}>All Posts</MenuItem>
                  <MenuItem onClick={() => handlePostOption('/posts?tab=null-users')}>Posts with Null Users</MenuItem>
                  <MenuItem onClick={() => handlePostOption('/mock-posts')}>
                    <PostAddIcon sx={{ mr: 1 }} />
                    Mock Posts
                  </MenuItem>
                </Menu>

                <Button sx={navButtonSx} onClick={handleUsersClick} endIcon={<ExpandMore sx={{ fontSize: 16 }} />}>
                  Users
                </Button>
                <Menu anchorEl={usersAnchor} open={Boolean(usersAnchor)} onClose={handleUsersClose}>
                  <MenuItem onClick={() => handleUserOption('/users?tab=all-users')}>All Users</MenuItem>
                  <MenuItem onClick={() => handleUserOption('/users?tab=users-list')}>Users List</MenuItem>
                  <MenuItem onClick={() => handleUserOption('/users?tab=god-view')}>God View</MenuItem>
                  <MenuItem onClick={() => handleUserOption('/users?tab=identity-options')}>Identity Options</MenuItem>
                  <MenuItem onClick={() => handleUserOption('/users?tab=profile-migration')}>Profile Migration</MenuItem>
                  <MenuItem onClick={() => handleUserOption('/mock-users')}>
                    <PersonAddIcon sx={{ mr: 1 }} />
                    Mock Users
                  </MenuItem>
                </Menu>

                <Button sx={navButtonSx} component={RouterLink} to="/notifications">Notifications</Button>
                <Button sx={navButtonSx} component={RouterLink} to="/reports">Reports</Button>
                <Button sx={navButtonSx} component={RouterLink} to="/analytics">Analytics</Button>
                <Button sx={navButtonSx} component={RouterLink} to="/enrichment">Enrichment</Button>
                <Button sx={navButtonSx} component={RouterLink} to="/interest-copy">Interest Copy</Button>
                <Button sx={navButtonSx} component={RouterLink} to="/place-claims">Place Claims</Button>
                <Button sx={navButtonSx} component={RouterLink} to="/security">Security</Button>
                <Button sx={navButtonSx} component={RouterLink} to="/country-boundaries">Boundaries</Button>
                <Button sx={navButtonSx} component={RouterLink} to="/api-health">API Health</Button>
                <Button sx={navButtonSx} component={RouterLink} to="/settings">Settings</Button>
              </Box>

              {/* Website-style account pill on the right. */}
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <Button
                  onClick={handleUserMenuClick}
                  endIcon={<ExpandMore sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: gridTokens.textPrimary,
                    border: `1px solid ${gridTokens.borderPrimary}`,
                    borderRadius: '999px',
                    px: 2,
                    py: 0.75,
                    '&:hover': {
                      borderColor: gridTokens.accent,
                      color: gridTokens.accent,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Account
                </Button>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={handleProfileClick}>
                    <PersonIcon sx={{ mr: 1 }} />
                    Profile
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon sx={{ mr: 1 }} />
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={drawerOpen} onClose={closeDrawer}>
        {drawerContent}
      </Drawer>
    </ThemeProvider>
  );
};

export default Navbar;
